import type {
  CheckRegisterFilterOptionsResponse,
  CheckRegisterQueryParams,
  CheckRegisterResponse,
  CheckRegisterItem,
  UpdateCheckStatusRequest,
  AddCheckNumberRequest,
} from '@/types/checkRegister.types';

import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import axiosInstance from './axiosInstance';
import type { GenericErrorResponse } from '@/types/api.types';

const BASE_URL = '/accounts/check-register';

export const getCheckRegisterData = async (params: CheckRegisterQueryParams): Promise<CheckRegisterResponse> => {
  const response = await axiosInstance.get<CheckRegisterResponse>(BASE_URL, {
    params: {
      ...params,
      clientIds: params.clientIds?.length ? JSON.stringify(params.clientIds) : undefined,
      invoiceNumbers: params.invoiceNumbers?.length ? JSON.stringify(params.invoiceNumbers) : undefined,
      checkNumbers: params.checkNumbers?.length ? JSON.stringify(params.checkNumbers) : undefined,
      fileNumbers: params.fileNumbers?.length ? JSON.stringify(params.fileNumbers) : undefined,
    },
  });
  return response.data;
};

export const useCheckRegisterFilterOptions = (enabled = true) => {
  return useQuery<CheckRegisterFilterOptionsResponse>({
    queryKey: ['check-register-filter-options'],
    queryFn: () =>
      axiosInstance.get<CheckRegisterFilterOptionsResponse>(`${BASE_URL}/filter-options`).then((res) => res.data),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
};

export const getCheckRegister = (
  params: CheckRegisterQueryParams,
  options?: { query?: Partial<UseQueryOptions<CheckRegisterResponse>> }
): {
  data: CheckRegisterItem[];
  totalRows: number;
  totalAmount?: string;
  totalChecks?: number;
  isFetching: boolean;
} => {
  const query = useQuery<CheckRegisterResponse>({
    queryFn: () => getCheckRegisterData(params),
    queryKey: ['check-register', params],
    ...options?.query,
  });

  const responseData = query.data;

  return {
    data: responseData?.data ?? [],
    totalRows: responseData?.totalRows ?? 0,
    totalAmount: responseData?.totalAmount,
    totalChecks: responseData?.totalChecks,
    isFetching: query.isFetching,
  };
};

export const useUpdateCheckStatus = () => {
  return useMutation<{ data: CheckRegisterItem[] }, AxiosError<GenericErrorResponse>, UpdateCheckStatusRequest>({
    mutationFn: async (data) => {
      const response = await axiosInstance.patch<{ data: CheckRegisterItem[] }>(`${BASE_URL}/status`, data);
      return response.data;
    },
  });
};

export const useAddCheckNumber = () => {
  return useMutation<{ message: string }, Error, { paymentId: string; data: AddCheckNumberRequest }>({
    mutationFn: async ({ paymentId, data }) => {
      const response = await axiosInstance.patch<{ message: string }>(`${BASE_URL}/${paymentId}/update`, data);
      return response.data;
    },
  });
};
